import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../ui/Panel';
import { InfoNotice } from '../ui/States';
import { EventDetail, FORMAT_LABEL, KIND_LABEL, eventDates, torontoToday } from '../../lib/events';
import { AttendanceNumbers } from './CountsEditor';
import { NotesEditor } from './Outreach';

/**
 * A PI meeting on one page: who came, where and when, and the agenda and notes. No tabs: a PI meeting has no
 * invitee list, hotel, tickets or money of its own, and its people are counted in the meeting it belongs to.
 */
export default function PiMeetingPage({
  detail,
  mutable,
  aside,
}: {
  detail: EventDetail;
  mutable: boolean;
  aside?: ReactNode;
}) {
  const e = detail.event;
  const parent = detail.parent;
  const past = (e.days[e.days.length - 1]?.date ?? '') < torontoToday();
  return (
    <>
      <PageHeader
        eyebrow={
          <span className="text-small">
            <Link className="underline" to="/events">
              All events
            </Link>
            {parent ? (
              <>
                {' '}
                · part of{' '}
                <Link className="underline" to={`/events/${parent.id}`}>
                  {parent.title}
                </Link>
              </>
            ) : null}
          </span>
        }
        title={e.title}
        lead={[KIND_LABEL.pi_meeting, eventDates(e), [e.venue, e.city].filter(Boolean).join(', ')]
          .filter(Boolean)
          .join(' · ')}
        aside={aside}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <AttendanceNumbers eventId={e.id} counts={e.counts} canEdit={mutable} past={past} />
        <Panel>
          <PanelHeader title="Details" />
          <PanelBody className="text-table">
            <dl className="grid grid-cols-[90px_1fr] gap-y-2">
              <dt className="text-ink-2">Place</dt>
              <dd>{[e.venue, e.city].filter(Boolean).join(', ') || '—'}</dd>
              <dt className="text-ink-2">Format</dt>
              <dd>{FORMAT_LABEL[e.mode] ?? '—'}</dd>
              <dt className="text-ink-2">When</dt>
              <dd>{eventDates(e)}</dd>
              <dt className="text-ink-2">Part of</dt>
              <dd>
                {parent ? (
                  <Link className="underline" to={`/events/${parent.id}`}>
                    {parent.title}
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
            </dl>
          </PanelBody>
        </Panel>
        <Panel className="lg:col-span-3">
          <PanelHeader title="Agenda and notes" />
          <PanelBody>
            <NotesEditor event={e} canEdit={mutable} />
          </PanelBody>
        </Panel>
      </div>
      {parent ? (
        <div className="mt-5">
          <InfoNotice>
            The people at this meeting are part of {parent.title}, so this headcount is not added to the attendance
            totals in the insights.
          </InfoNotice>
        </div>
      ) : null}
    </>
  );
}
